// app-tour.service.ts - Cleaned and improved with mobile detection

import { Injectable } from '@angular/core';
import { TourService } from './tour.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {Observable, take} from 'rxjs';
import * as TourActions from '../state/tour/tour.actions';
import { selectTourActive, selectTourCompleted } from '../state/tour/tour.selectors';
import { AuthService } from "./auth.service";

@Injectable({
  providedIn: 'root'
})
export class AppTourService {
  // Expose observables for components
  tourActive$: Observable<boolean>;
  tourCompleted$: Observable<boolean>;

  // Flag to track forced tour starts
  private forceTourStartFlag = 'salesommo_force_tour_start';

  // Track page-specific tour completion
  private dashboardTourCompletedKey = 'salesommo_tour_completed_dashboard';
  private icpTourCompletedKey = 'salesommo_tour_completed_icp'

  // User setup tracking
  private userChecked = false;
  private userSetupCompleted = false;

  // Add a property to track if tour is in progress across pages
  private tourInProgressKey = 'salesommo_tour_in_progress';

  constructor(
    private tourService: TourService,
    private router: Router,
    private store: Store,
    private authService: AuthService
  ) {
    // Connect to store selectors
    this.tourActive$ = this.store.select(selectTourActive);
    this.tourCompleted$ = this.store.select(selectTourCompleted);

    // Initialize user check with performance enhancement
    this.fastInitializeUserCheck();

    // Only initialize tours if not mobile
    if (!this.isMobileDevice()) {
      // Initialize tour steps
      this.initializeTours();

      // Pre-optimize tour loading
      this.preCacheTourElements();
    } else {
      console.log('Mobile device detected - tour initialization skipped');
    }
  }

  /**
   * Check if current device is mobile
   */
  private isMobileDevice(): boolean {
    // Check user agent
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isMobileUserAgent = mobileRegex.test(userAgent);

    // Also check viewport width
    const isMobileWidth = window.innerWidth <= 768;

    // Check touch capability as additional indicator
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Consider it mobile if any strong indicators are present
    return isMobileUserAgent || (isMobileWidth && isTouchDevice);
  }

  /**
   * Public method to check if mobile (for use in components)
   */
  isMobile(): boolean {
    return this.isMobileDevice();
  }

  /**
   * Faster user check initialization
   */
  private fastInitializeUserCheck(): void {
    // Check localStorage first (much faster than API)
    const setupCompletedInStorage = localStorage.getItem('user_setup_completed') === 'true';

    if (setupCompletedInStorage) {
      // If user has completed setup according to localStorage, use that
      this.userChecked = true;
      this.userSetupCompleted = true;

      // Mark all tours as completed immediately
      localStorage.setItem('appTourCompleted', 'true');
      localStorage.setItem(this.dashboardTourCompletedKey, 'true');
      localStorage.setItem(this.icpTourCompletedKey, 'true');

      // console.log('User setup marked as completed from localStorage');
    }

    // Also check API, but don't block on it
    this.authService.authUser().subscribe(user => {
      this.userChecked = true;
      this.userSetupCompleted = user && user.has_completed_setup === 1;

      // If user has completed setup, mark all tours as completed
      if (this.userSetupCompleted) {
        localStorage.setItem('appTourCompleted', 'true');
        localStorage.setItem(this.dashboardTourCompletedKey, 'true');
        localStorage.setItem(this.icpTourCompletedKey, 'true');
      }
    });
  }

  /**
   * Pre-cache tour elements without creating a tour instance
   */
  private preCacheTourElements(): void {
    // Skip if mobile
    if (this.isMobileDevice()) return;

    // Pre-create necessary styles
    const styleElement = document.createElement('style');
    styleElement.textContent = `
    .tour-fast-transitions .shepherd-element {
      transition-duration: 0.15s !important;
    }
    body.tour-active #icp-optimization-form {
      visibility: visible !important;
      opacity: 1 !important;
      max-height: 80vh !important;
      overflow-y: auto !important;
      z-index: 10000 !important;
      position: relative !important;
    }
    .shepherd-element {
      opacity: 0;
      transform: translateY(5px);
      animation: shepherd-fade-in 0.15s ease forwards !important;
    }
    @keyframes shepherd-fade-in {
      0% { opacity: 0; transform: translateY(5px); }
      100% { opacity: 1; transform: translateY(0); }
    }
  `;
    document.head.appendChild(styleElement);
  }

  /**
   * Initialize user setup status check
   */
  private initializeUserCheck(): void {
    // Check user setup status and store it
    this.authService.authUser().subscribe(user => {
      this.userChecked = true;
      this.userSetupCompleted = user && user.has_completed_setup === 1;

      // If user has completed setup, mark all tours as completed
      if (this.userSetupCompleted) {
        localStorage.setItem('appTourCompleted', 'true');
        localStorage.setItem(this.dashboardTourCompletedKey, 'true');
        localStorage.setItem(this.icpTourCompletedKey, 'true');
      }
    });
  }

  /**
   * Initialize tour steps for all pages
   */
  private initializeTours(): void {
    // Skip if mobile
    if (this.isMobileDevice()) return;

    // Dashboard tour steps
    const dashboardSteps = [
      {
        id: 'dashboard-step-1',
        attachTo: {
          element: '#icp-optimization-section',
          on: 'left-start'
        },
        title: 'Complete Your ICP Profile',
        text: 'This section shows your ICP (Ideal Customer Profile) optimization level. Complete your profile to get more accurate results!',
        highlightClass: 'shepherd-highlighted',
        scrollTo: { behavior: 'auto', block: 'center' },
        popperOptions: {
          modifiers: [
            {
              name: 'offset',
              options: { offset: [0, 20] }
            },
            {
              name: 'flip',
              options: { fallbackPlacements: ['right-start', 'top', 'bottom'] }
            }
          ]
        },
        buttons: [
          {
            text: 'Exit Tour',
            classes: 'shepherd-button-exit',
            action: () => {
              // Debug localStorage state BEFORE changes
              console.log('Before Exit Tour - localStorage state:', {
                dashboardTourCompleted: localStorage.getItem(this.dashboardTourCompletedKey),
                icpTourCompleted: localStorage.getItem(this.icpTourCompletedKey)
              });

              // Clean up any custom overlays
              const customOverlay = document.querySelector('.custom-tour-overlay');
              if (customOverlay && customOverlay.parentNode) {
                customOverlay.parentNode.removeChild(customOverlay);
              }

              // Get current route
              const currentRoute = this.router.url;
              console.log(`Exiting tour on route: ${currentRoute}`);

              // CRITICAL: Explicitly force key to be set first
              if (currentRoute === '/' || currentRoute === '/dashboard') {
                // DIRECT localStorage set - simplest and most reliable
                localStorage.setItem(this.dashboardTourCompletedKey, 'true');

                // Verify the write immediately
                console.log(`Directly set ${this.dashboardTourCompletedKey} = true, verification:`,
                  localStorage.getItem(this.dashboardTourCompletedKey));
              } else if (currentRoute === '/icp-optimization') {
                localStorage.setItem(this.icpTourCompletedKey, 'true');
              }

              // Clear the in-progress flag
              this.setTourInProgress(false);

              // Now call completeTourForPage as a backup
              this.completeTourForPage(currentRoute);

              // Debug localStorage state AFTER changes
              console.log('After Exit Tour - localStorage state:', {
                dashboardTourCompleted: localStorage.getItem(this.dashboardTourCompletedKey),
                icpTourCompleted: localStorage.getItem(this.icpTourCompletedKey)
              });

              // Cancel the tour
              this.tourService.cancelTour();
              this.store.dispatch(TourActions.cancelTour());
              this.cleanupTourOverlay();
            }
          },
          {
            text: 'Next',
            action: () => this.navigateToIcpFromTour()
          }
        ]
      }
    ];

    // ICP Optimization page steps
    const icpOptimizationSteps = [
      {
        id: 'icp-step-1',
        attachTo: {
          element: '#icp-optimization-form',
          on: 'left-start'
        },
        title: 'Complete Your ICP Form',
        text: 'Fill out this form to improve your prospect matching. The more details you provide, the better your results will be.',
        highlightClass: 'shepherd-highlighted',
        scrollTo: { behavior: 'auto', block: 'center' },
        popperOptions: {
          modifiers: [
            {
              name: 'offset',
              options: { offset: [0, 20] }
            },
            {
              name: 'flip',
              options: { fallbackPlacements: ['right-start', 'top', 'bottom'] }
            }
          ]
        },
        buttons: [
          {
            text: 'Exit Tour',
            classes: 'shepherd-button-exit',
            action: () => {
              // Clean up any custom overlays
              const customOverlay = document.querySelector('.custom-tour-overlay');
              if (customOverlay && customOverlay.parentNode) {
                customOverlay.parentNode.removeChild(customOverlay);
              }

              // Mark ICP tour as completed
              this.completeTourForPage('/icp-optimization');

              // Clear the in-progress flag
              this.setTourInProgress(false);

              // Cancel the tour
              this.tourService.cancelTour();
              this.store.dispatch(TourActions.cancelTour());
              this.cleanupTourOverlay();
            }
          }
        ]
      }
    ];

    // Register steps for each page
    this.tourService.registerTourStepsForPage('/', dashboardSteps);
    this.tourService.registerTourStepsForPage('/dashboard', dashboardSteps);
    this.tourService.registerTourStepsForPage('/icp-optimization', icpOptimizationSteps);
  }

  /**
   * Check if a tour is currently in progress across pages
   */
  isTourInProgress(): boolean {
    return localStorage.getItem(this.tourInProgressKey) === 'true';
  }

  /**
   * Set tour in progress state
   */
  setTourInProgress(inProgress: boolean): void {
    if (inProgress) {
      localStorage.setItem(this.tourInProgressKey, 'true');
      console.log('Tour marked as in progress');
    } else {
      localStorage.removeItem(this.tourInProgressKey);
      console.log('Tour marked as NOT in progress');
    }
  }

  /**
   * Clean up tour overlay elements
   */
  private cleanupTourOverlay(): void {
    // Remove shepherd modal class from body
    document.body.classList.remove('shepherd-modal-is-visible');

    // Find and remove any overlay containers
    const overlays = document.querySelectorAll('.shepherd-modal-overlay-container');
    overlays.forEach(overlay => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    });

    // Re-enable scrolling on the body
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    console.log('Tour overlay cleaned up');
  }

  /**
   * Check if tour should be shown based on user setup status
   */
  async shouldShowTour(): Promise<boolean> {
    // First check if mobile
    if (this.isMobileDevice()) {
      console.log('Mobile device - tour should not be shown');
      return false;
    }

    // If we haven't checked the user yet, wait for the check
    if (!this.userChecked) {
      console.log('Waiting for user check to complete...');
      return new Promise<boolean>((resolve) => {
        // Try for up to 3 seconds then default to showing the tour
        let attempts = 0;
        const maxAttempts = 30;
        const checkInterval = setInterval(() => {
          attempts++;
          if (this.userChecked || attempts >= maxAttempts) {
            clearInterval(checkInterval);
            console.log('User check complete or timeout reached', {
              userChecked: this.userChecked,
              userSetupCompleted: this.userSetupCompleted,
              attempts
            });
            resolve(!this.userSetupCompleted);
          }
        }, 100);
      });
    }

    // We've already checked the user, return the result immediately
    console.log('User setup status already checked:', { hasCompletedSetup: this.userSetupCompleted });
    return !this.userSetupCompleted;
  }

  /**
   * Navigate from dashboard to ICP page as part of the tour
   */
  private navigateToIcpFromTour(): void {
    this.router.navigate(['/icp-optimization']);
  }

  /**
   * Start the tour on the current page - with added protection
   */
  async startTour(route?: string): Promise<void> {
    // Check if mobile first
    if (this.isMobileDevice()) {
      console.log('Tour blocked - mobile device detected');
      return;
    }

    // IMMEDIATE CHECK: Check if this tour is already completed
    const currentRoute = this.router.url;
    const isCompleted = this.isPageTourCompleted(currentRoute);

    if (isCompleted) {
      console.log(`Tour for ${currentRoute} already completed, not starting`);
      return;
    }

    console.log(`Starting tour on current page: ${currentRoute}`);

    // Set the tour as in progress
    this.setTourInProgress(true);

    // Use NgRx to start the tour
    this.store.dispatch(TourActions.startTour({ route }));
  }

  /**
   * Start tour specifically for ICP optimization page
   */
  async startIcpTour(): Promise<void> {
    // Check if mobile first
    if (this.isMobileDevice()) {
      console.log('ICP tour blocked - mobile device detected');
      return;
    }

    console.log('Starting ICP tour with accelerated initialization');

    // Set the tour as in progress immediately
    this.setTourInProgress(true);

    // Apply tour-active class to body immediately
    document.body.classList.add('tour-active');

    // Ensure it's the right page
    if (this.router.url !== '/icp-optimization') {
      console.warn('Attempted to start ICP tour on wrong page');
      return;
    }

    // Fast check if this tour should be skipped
    const shouldSkip =
      localStorage.getItem('appTourCompleted') === 'true' ||
      localStorage.getItem(this.icpTourCompletedKey) === 'true';

    // However, if we're forcing the tour start, override the skip
    const isForced = localStorage.getItem(this.forceTourStartFlag) === 'true';

    if (shouldSkip && !isForced) {
      console.log('ICP tour should be skipped, not showing');
      return;
    }

    // Pre-prepare critical elements for tour
    this.prepareIcpFormForTour();

    // Ensure no existing tour is running
    if (this.tourService.isActiveTour()) {
      this.tourService.cancelTour();
    }

    // Set the current page explicitly without delay
    this.tourService['currentPageSubject'].next('/icp-optimization');

    // Dispatch action to start tour
    this.store.dispatch(TourActions.startTour({ route: '/icp-optimization' }));

    // Clear force flag if present
    if (isForced) {
      this.clearForceTourStart();
    }
  }

  /**
   * New method to prepare ICP form for tour immediately
   */
  private prepareIcpFormForTour(): void {
    // Find and prepare the ICP form element
    const formElement = document.querySelector('#icp-optimization-form') as HTMLElement;
    if (formElement) {
      // Apply critical styles right away for instant visibility
      Object.assign(formElement.style, {
        visibility: 'visible',
        opacity: '1',
        overflow: 'auto',
        maxHeight: '80vh',
        zIndex: '10000',
        position: 'relative'
      });

      // Add necessary classes
      formElement.classList.add('overflow-y-auto', 'max-h-[80vh]', 'thin-scrollbar');

      // Remove any classes that might be hiding it
      formElement.classList.remove('invisible', 'opacity-0');

      console.log('ICP form prepared for tour');
    }
  }

  /**
   * Cancel the current tour
   */
  cancelTour(): void {
    console.log('Canceling tour with enhanced cleanup');

    // Get the current route before cancelling
    const currentRoute = this.router.url;
    console.log(`Canceling tour on route: ${currentRoute}`);

    // Mark the current page's tour as completed when cancelled
    this.completeTourForPage(currentRoute);

    // Log to verify what got saved
    console.log(`Dashboard tour completed: ${localStorage.getItem(this.dashboardTourCompletedKey)}`);
    console.log(`ICP tour completed: ${localStorage.getItem(this.icpTourCompletedKey)}`);

    // Clear the in-progress flag
    this.setTourInProgress(false);

    // Cancel via tour service
    this.tourService.cancelTour();

    // Dispatch the action to update state
    this.store.dispatch(TourActions.cancelTour());

    // Additional cleanup
    this.cleanupAnyRemainingOverlay();
  }

  /**
   * Cleanup any remaining overlay elements
   */
  private cleanupAnyRemainingOverlay(): void {
    // Remove modal class from body
    document.body.classList.remove('shepherd-modal-is-visible');

    // Find and remove any overlay containers
    const overlays = document.querySelectorAll('.shepherd-modal-overlay-container');
    overlays.forEach(overlay => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    });

    // Find and remove custom overlays
    const customOverlays = document.querySelectorAll('.custom-tour-overlay');
    customOverlays.forEach(overlay => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    });

    // Reset any fixed positioning on body
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    console.log('Additional overlay cleanup completed');
  }

  /**
   * Mark a specific tour page as completed
   */
  completeTourForPage(route: string): void {
    console.log(`Marking tour for ${route} as completed`);

    if (route === '/icp-optimization') {
      localStorage.setItem(this.icpTourCompletedKey, 'true');
      // Verify write
      const verifyIcp = localStorage.getItem(this.icpTourCompletedKey);
      console.log(`ICP tour marked as completed: ${this.icpTourCompletedKey} = ${verifyIcp}`);
    } else if (route === '/' || route === '/dashboard') {
      localStorage.setItem(this.dashboardTourCompletedKey, 'true');
      // Verify write
      const verifyDashboard = localStorage.getItem(this.dashboardTourCompletedKey);
      console.log(`Dashboard tour marked as completed: ${this.dashboardTourCompletedKey} = ${verifyDashboard}`);
    } else {
      console.warn(`Unknown route for tour completion: ${route}`);
    }

    // Check both values
    const dashboardCompleted = localStorage.getItem(this.dashboardTourCompletedKey) === 'true';
    const icpCompleted = localStorage.getItem(this.icpTourCompletedKey) === 'true';

    // Log the overall state
    console.log(`Tour completion state: Dashboard=${dashboardCompleted}, ICP=${icpCompleted}`);

    // Set overall completion if all individual tours are done
    if (dashboardCompleted && icpCompleted) {
      localStorage.setItem('appTourCompleted', 'true');
      console.log('All tours marked as completed');
    }
  }

  /**
   * Complete the current tour
   */
  completeTour(): void {
    console.log('Completing tour and marking as completed');

    // Dispatch the action to complete the tour - effects will handle localStorage
    this.store.dispatch(TourActions.completeTour());

    // Directly cancel the active tour
    if (this.tourService.isActiveTour()) {
      this.tourService.getTour().complete();
    }
  }

  /**
   * Reset tour state
   */
  resetTourState(): void {
    // Dispatch the action to reset tour status
    this.store.dispatch(TourActions.resetTourStatus());

    // Clear all localStorage items
    localStorage.removeItem('appTourCompleted');
    localStorage.removeItem(this.dashboardTourCompletedKey);
    localStorage.removeItem(this.icpTourCompletedKey);
    localStorage.removeItem(this.forceTourStartFlag);

    // Clear any tour continuation data
    this.tourService.clearTourContinuation();
  }

  /**
   * Get the underlying tour instance
   */
  getTourInstance(): any {
    return this.tourService.getTour();
  }

  /**
   /**
   * Check if tour is currently active
   */
  isTourActive(): boolean {
    return this.tourService.isActiveTour();
  }

  /**
   * Get the current route
   */
  getCurrentRoute(): string {
    return this.router.url;
  }

  /**
   * Check if overall tour has been completed
   */
  isTourCompleted(): boolean {
    return localStorage.getItem('appTourCompleted') === 'true';
  }

  /**
   * Check if specific page tour has been completed
   */
  isPageTourCompleted(route: string): boolean {
    if (route === '/icp-optimization') {
      return localStorage.getItem(this.icpTourCompletedKey) === 'true';
    } else if (route === '/' || route === '/dashboard') {
      return localStorage.getItem(this.dashboardTourCompletedKey) === 'true';
    }
    return false;
  }

  /**
   * Check if tour should be force-started
   */
  shouldForceTourStart(): boolean {
    return localStorage.getItem(this.forceTourStartFlag) === 'true';
  }

  /**
   * Clear the force start flag
   */
  clearForceTourStart(): void {
    localStorage.removeItem(this.forceTourStartFlag);
  }

  /**
   * Reset tour state when user logs in or out
   */
  resetTourStateForUserChange(): void {
    // Reset cached user state
    this.userChecked = false;
    this.userSetupCompleted = false;

    // Clear localStorage items
    this.resetTourState();

    // Re-check user status
    this.initializeUserCheck();
  }

  /**
   * Debug method to log current tour state
   */
  debugTourState(): void {
    const currentRoute = this.router.url;
    const dashboardTourCompleted = localStorage.getItem(this.dashboardTourCompletedKey) === 'true';
    const icpTourCompleted = localStorage.getItem(this.icpTourCompletedKey) === 'true';
    const overallTourCompleted = localStorage.getItem('appTourCompleted') === 'true';
    const tourInProgress = this.isTourInProgress();
    const tourActive = this.isTourActive();
    const userSetupCompleted = localStorage.getItem('user_setup_completed') === 'true';
    const isMobile = this.isMobileDevice();

    console.group('Tour Debug Info');
    console.log('Current route:', currentRoute);
    console.log('Dashboard tour completed:', dashboardTourCompleted);
    console.log('ICP tour completed:', icpTourCompleted);
    console.log('Overall tour completed:', overallTourCompleted);
    console.log('Tour in progress:', tourInProgress);
    console.log('Tour active:', tourActive);
    console.log('User setup completed:', userSetupCompleted);
    console.log('Is mobile device:', isMobile);
    console.groupEnd();
  }

}
